import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../apiHelpers';
import { Memory } from './types';
import { SkeletonRows } from './styles';
import { buttonClass } from '../components/ui/Button';
import Chip, { ChipGroup } from '../components/ui/Chip';
import { TextArea } from '../components/ui/Field';
import Select from '../components/ui/Select';
import Tag from '../components/ui/Tag';
import Dialog from '../components/ui/Dialog';
import EmptyState from '../components/ui/EmptyState';
import FadeIn from '../components/ui/FadeIn';
import Appear from '../components/ui/Appear';
import { ListRow, ListStack } from '../components/ui/ListRow';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';

interface MemoriesModalProps {
  onClose: () => void;
}

const CATEGORIES = ['fleet_observation', 'business_rule', 'hardware_note', 'firmware_note', 'diagnostic_pattern'];

const MemoriesModal: React.FC<MemoriesModalProps> = ({ onClose }) => {
  const api = useApi();
  const confirm = useConfirm();
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(true);
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState('fleet_observation');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<string>('all');

  const fetchMemories = useCallback(async () => {
    setLoading(true);
    await api.get('/analysis/memories', {
      onSuccess: (data: any) => setMemories(data.memories || []),
    });
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchMemories(); }, [fetchMemories]);

  const addMemory = async () => {
    if (!newContent.trim()) return;
    setSaving(true);
    await api.post('/analysis/memories', {
      body: JSON.stringify({ content: newContent.trim(), category: newCategory, source: 'human' }),
      onSuccess: () => {
        setNewContent('');
        fetchMemories();
      },
    });
    setSaving(false);
  };

  const toggleMemory = async (mem: Memory) => {
    await api.patch(`/analysis/memories/${mem.id}`, {
      body: JSON.stringify({ active: !mem.active }),
      onSuccess: fetchMemories,
    });
  };

  const deleteMemory = async (id: number) => {
    if (!(await confirm({ message: 'Delete this memory? The analyst stops using it right away.', confirmLabel: 'Delete', tone: 'danger' }))) return;
    await api.delete(`/analysis/memories/${id}`, { onSuccess: fetchMemories });
  };

  const filtered = filter === 'all' ? memories : memories.filter(m => m.category === filter);
  const categoryLabel = (c: string) => {
    const words = c.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title="Knowledge"
      meta={<span className="tabular-nums">{memories.length} memories, {memories.filter(m => m.active).length} active</span>}
      closeHint="Close knowledge"
      dirty={newContent.trim() !== ''}
    >
      {/* Add new */}
      <div className="-mx-5 flex flex-col gap-2 bg-surface-container px-5 py-3">
        <Select
          label="Category"
          width="fit"
          className="self-start"
          value={newCategory}
          onChange={setNewCategory}
          options={CATEGORIES.map(c => ({ key: c, label: categoryLabel(c) }))}
        />
        <div className="flex min-w-0 items-end gap-2">
          <TextArea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="Add something the analyst should know, like: AM32 v2.189 ESC current telemetry does not work anywhere in the fleet"
            aria-label="New memory"
            rows={2}
            className="flex-1"
          />
          <button
            type="button"
            onClick={addMemory}
            disabled={saving || !newContent.trim()}
            className={buttonClass('filled')}
          >
            {saving ? 'Adding' : 'Add'}
          </button>
        </div>
      </div>

      {/* Filter */}
      <ChipGroup label="Filter by category" className="py-2.5">
        {['all', ...CATEGORIES].map(c => (
          <Chip key={c} pressed={filter === c} onClick={() => setFilter(c)}>
            {c === 'all' ? 'All' : categoryLabel(c)}
          </Chip>
        ))}
      </ChipGroup>

      {/* List */}
      <FadeIn key={loading ? 'loading' : filtered.length === 0 ? 'empty' : 'list'}>
      {loading ? (
        <SkeletonRows rows={4} />
      ) : filtered.length === 0 ? (
        <EmptyState variant="area">
          {filter === 'all'
            ? 'No memories yet. Add the first thing the analyst should know above.'
            : 'Nothing in this category yet. Add one above, or pick another category.'}
        </EmptyState>
      ) : (
        <ListStack
          items={filtered}
          getKey={(mem: Memory) => String(mem.id)}
          on="panel"
          spacing="loose"
          label="Memories"
          renderRow={(mem: Memory) => (
            <ListRow className={mem.active ? '' : 'opacity-70'}>
              <div className="min-w-0">
                <div className="mb-1 flex min-w-0 items-center gap-2 text-xs">
                  <Tag variant="code">
                    {mem.source === 'agent' ? 'Agent' : 'Human'}
                  </Tag>
                  <span className="truncate text-on-surface-variant">{categoryLabel(mem.category)}</span>
                  <Appear show={!mem.active} className="text-on-surface-variant">Off</Appear>
                  <span className="ml-auto truncate text-on-surface-variant">{mem.added_by}</span>
                </div>
                <p className={`m-0 text-sm leading-normal ${mem.active ? 'text-on-surface' : 'text-on-surface-variant'}`}>
                  {mem.content}
                </p>
                <div className="-ml-2 mt-1.5 flex gap-1">
                  <button
                    type="button"
                    onClick={() => toggleMemory(mem)}
                    className={buttonClass('quiet', 'sm')}
                  >
                    {mem.active ? 'Turn off' : 'Turn on'}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteMemory(mem.id)}
                    className={buttonClass('danger-quiet', 'sm')}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </ListRow>
          )}
        />
      )}
      </FadeIn>
    </Dialog>
  );
};

export default MemoriesModal;
